const SEVERITY_PENALTY = {
  low: 2,
  medium: 5,
  high: 10,
  critical: 20,
};

const SIGNAL_CONFIG = {
  tab_switch: { confidence: 0.85, severity: 'high', tier: 'browser', message: 'Candidate left the interview tab' },
  paste: { confidence: 0.9, severity: 'high', tier: 'browser', message: 'Code pasted from external source' },
  fullscreen_exit: { confidence: 0.65, severity: 'medium', tier: 'browser', message: 'Candidate exited fullscreen mode' },
  devtools_open: { confidence: 0.95, severity: 'high', tier: 'browser', message: 'Developer tools may be open' },
  context_menu: { confidence: 0.3, severity: 'low', tier: 'browser', message: 'Right-click context menu opened' },
  screen_share_stopped: { confidence: 0.7, severity: 'medium', tier: 'browser', message: 'Screen sharing was stopped' },
  multi_monitor: { confidence: 0.6, severity: 'medium', tier: 'browser', message: 'Multiple monitors detected' },
  split_screen: { confidence: 0.55, severity: 'medium', tier: 'browser', message: 'Window resized to split-screen size' },
  idle: { confidence: 0.5, severity: 'medium', tier: 'browser', message: 'No keyboard or mouse activity detected' },
  screenshot: { confidence: 0.45, severity: 'medium', tier: 'browser', message: 'Print screen key pressed' },
  rapid_typing: { confidence: 0.75, severity: 'medium', tier: 'behavioral', message: 'Unusually fast bulk typing detected' },
  code_similarity_spike: { confidence: 0.7, severity: 'medium', tier: 'behavioral', message: 'Large code block appeared without proportional typing' },
  multiple_paste: { confidence: 0.88, severity: 'high', tier: 'behavioral', message: 'Multiple paste events in short window' },
  ai_agent_help: { confidence: 0.82, severity: 'high', tier: 'behavioral', message: 'AI-assisted typing pattern detected' },
  multiple_faces: { confidence: 0.88, severity: 'high', tier: 'vision', message: 'Multiple faces detected in camera' },
  phone_detected: { confidence: 0.85, severity: 'high', tier: 'vision', message: 'Phone or secondary device detected' },
  no_face: { confidence: 0.55, severity: 'medium', tier: 'vision', message: 'No face visible in camera' },
  sustained_look_away: { confidence: 0.68, severity: 'medium', tier: 'vision', message: 'Sustained looking away from screen detected' },
  repeated_side_glance: { confidence: 0.62, severity: 'medium', tier: 'vision', message: 'Repeated side-glance pattern detected' },
  linear_reading_pattern: { confidence: 0.5, severity: 'low', tier: 'vision', message: 'Linear eye/head movement pattern detected' },
  multiple_voices: { confidence: 0.72, severity: 'medium', tier: 'audio', message: 'Secondary voice or background speaker detected' },
  vision_unavailable: { confidence: 1, severity: 'low', tier: 'vision', message: 'Vision proctoring unavailable on this device' },
};

const AI_SIGNATURE_PATTERNS = [
  /^here'?s\b/i,
  /^\/\/\s*solution:/i,
  /^\/\*\s*solution/i,
  /as an ai/i,
];

function stdDev(values) {
  if (!values.length) return 0;
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  const variance = values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / values.length;
  return Math.sqrt(variance);
}

export default class ProctorService {
  constructor({ onEvent } = {}) {
    this.onEvent = onEvent || (() => {});
    this.trustScore = 100;
    this.events = [];
    this.eventCount = 0;
    this.tabAwayMs = 0;
    this.lastDedup = new Map();
    this.mounted = false;

    this.keystrokes = [];
    this.pasteTimestamps = [];
    this.lastCode = '';
    this.lastTabSwitchAt = 0;
    this.lastActivityAt = Date.now();
    this.rapidTypingChars = 0;
    this.rapidTypingStartedAt = 0;
    this.backspacesInBlock = 0;
    this.charsInBlock = 0;

    this.boundHandlers = {};
    this.idleTimer = null;
    this.editorDom = null;
    this.editorPasteHandler = null;
    this.editorKeydownHandler = null;
    this.editorCopyHandler = null;
    this.lastInternalCopy = '';
  }

  mount() {
    if (this.mounted || typeof window === 'undefined') return;
    this.mounted = true;

    this.boundHandlers.visibility = () => {
      if (document.visibilityState === 'hidden') {
        this.tabHiddenAt = Date.now();
        this.emit('tab_switch');
      } else if (this.tabHiddenAt) {
        this.tabAwayMs += Date.now() - this.tabHiddenAt;
        this.tabHiddenAt = 0;
      }
    };

    this.boundHandlers.blur = () => this.emit('tab_switch');
    this.boundHandlers.fullscreen = () => {
      if (!document.fullscreenElement) this.emit('fullscreen_exit');
    };
    this.boundHandlers.contextmenu = (event) => {
      if (this.editorDom?.contains(event.target)) {
        this.emit('context_menu');
      }
    };
    this.boundHandlers.resize = () => {
      const widthGap = window.outerWidth - window.innerWidth;
      const heightGap = window.outerHeight - window.innerHeight;
      if (widthGap > 160 || heightGap > 160) {
        this.emit('devtools_open', { widthGap, heightGap });
      }
      const widthRatio = window.innerWidth / window.screen.width;
      const heightRatio = window.innerHeight / window.screen.height;
      if (widthRatio < 0.5 || heightRatio < 0.5) {
        this.emit('split_screen', { widthRatio, heightRatio });
      }
    };
    this.boundHandlers.keydown = (event) => {
      this.lastActivityAt = Date.now();
      if (event.key === 'PrintScreen') {
        this.emit('screenshot');
      }
    };
    this.boundHandlers.activity = () => {
      this.lastActivityAt = Date.now();
    };

    document.addEventListener('visibilitychange', this.boundHandlers.visibility);
    window.addEventListener('blur', this.boundHandlers.blur);
    document.addEventListener('fullscreenchange', this.boundHandlers.fullscreen);
    document.addEventListener('contextmenu', this.boundHandlers.contextmenu);
    window.addEventListener('resize', this.boundHandlers.resize);
    window.addEventListener('keydown', this.boundHandlers.keydown);
    window.addEventListener('mousemove', this.boundHandlers.activity);
    window.addEventListener('mousedown', this.boundHandlers.activity);

    if (window.screen?.isExtended) {
      this.emit('multi_monitor');
    }

    this.idleTimer = window.setInterval(() => {
      if (Date.now() - this.lastActivityAt > 60000) {
        this.emit('idle', { idleSeconds: Math.round((Date.now() - this.lastActivityAt) / 1000) });
      }
    }, 15000);

    this.boundHandlers.resize();
  }

  attachEditorDom(dom) {
    if (!dom || this.editorDom === dom) return;
    this.detachEditorDom();
    this.editorDom = dom;

    this.editorPasteHandler = (event) => {
      const pasted = event.clipboardData?.getData('text') || '';
      this.handlePaste(pasted);
    };
    this.editorKeydownHandler = (event) => {
      this.handleEditorKeydown(event);
    };
    // Track text copied from within the editor so internal copy-paste is not flagged.
    // This is a fallback for browsers where clipboard read works in the copy event.
    this.editorCopyHandler = () => {
      const selection = window.getSelection();
      if (selection) {
        const text = selection.toString();
        if (text) this.lastInternalCopy = text;
      }
    };

    dom.addEventListener('paste', this.editorPasteHandler);
    dom.addEventListener('keydown', this.editorKeydownHandler);
    dom.addEventListener('copy', this.editorCopyHandler);
  }

  /**
   * Called by the Monaco editor's onCopy action to register an internal copy.
   * This is more reliable than listening to the DOM copy event for Monaco.
   */
  recordInternalCopy(text = '') {
    if (text) {
      this.lastInternalCopy = text;
    }
  }

  detachEditorDom() {
    if (!this.editorDom) return;
    if (this.editorPasteHandler) {
      this.editorDom.removeEventListener('paste', this.editorPasteHandler);
    }
    if (this.editorKeydownHandler) {
      this.editorDom.removeEventListener('keydown', this.editorKeydownHandler);
    }
    if (this.editorCopyHandler) {
      this.editorDom.removeEventListener('copy', this.editorCopyHandler);
    }
    this.editorDom = null;
    this.editorPasteHandler = null;
    this.editorKeydownHandler = null;
    this.editorCopyHandler = null;
  }

  handlePaste(pastedText = '') {
    // Allow copy-paste within the IDE: if the pasted text matches what was
    // last copied from inside the editor, skip the external-paste signal.
    if (pastedText && pastedText === this.lastInternalCopy) {
      return;
    }

    const now = Date.now();
    this.pasteTimestamps = this.pasteTimestamps.filter((timestamp) => now - timestamp < 60000);
    this.pasteTimestamps.push(now);
    this.emit('paste', {
      length: pastedText.length,
      preview: pastedText.slice(0, 120),
    });
    if (this.pasteTimestamps.length >= 3) {
      this.emit('multiple_paste', { count: this.pasteTimestamps.length });
    }
  }

  handleEditorKeydown(event) {
    const now = Date.now();
    const isDeletion = event.key === 'Backspace' || event.key === 'Delete';
    if (!isDeletion && event.key.length === 1) {
      this.keystrokes.push({ at: now, char: event.key });
      if (this.keystrokes.length > 500) {
        this.keystrokes = this.keystrokes.slice(-500);
      }
      this.charsInBlock += 1;
      this.trackRapidTyping(now);
    } else if (isDeletion) {
      this.backspacesInBlock += 1;
    }
    this.lastActivityAt = now;
  }

  trackRapidTyping(now) {
    if (!this.rapidTypingStartedAt) {
      this.rapidTypingStartedAt = now;
      this.rapidTypingChars = 0;
    }
    this.rapidTypingChars += 1;
    const elapsed = now - this.rapidTypingStartedAt;
    if (elapsed >= 3000) {
      const charsPerMinute = (this.rapidTypingChars / elapsed) * 60000;
      if (charsPerMinute > 800) {
        this.emit('rapid_typing', { charsPerMinute: Math.round(charsPerMinute) });
      }
      this.rapidTypingStartedAt = now;
      this.rapidTypingChars = 0;
    }
  }

  recordCodeChange(nextCode = '') {
    const previous = this.lastCode;
    this.lastCode = nextCode;
    if (!previous) return;

    const charsAdded = Math.max(0, nextCode.length - previous.length);
    if (charsAdded < 40) return;

    const recentKeystrokes = this.keystrokes.filter((item) => Date.now() - item.at < 5000).length;
    if (charsAdded > 100 && recentKeystrokes < charsAdded * 0.3) {
      this.emit('code_similarity_spike', { charsAdded, recentKeystrokes });
    }

    const intervals = this.keystrokes
      .slice(-Math.min(this.keystrokes.length, charsAdded + 5))
      .map((item, index, list) => (index === 0 ? null : item.at - list[index - 1].at))
      .filter((value) => typeof value === 'number' && value > 0);

    const cadence = stdDev(intervals);
    const tabCorrelation = this.lastTabSwitchAt && Date.now() - this.lastTabSwitchAt < 15000;
    const aiSignature = AI_SIGNATURE_PATTERNS.some((pattern) => pattern.test(nextCode.slice(-200)));
    const zeroBackspaces = this.charsInBlock >= 100 && this.backspacesInBlock === 0;

    let aiScore = 0;
    if (zeroBackspaces) aiScore += 0.3;
    if (cadence > 0 && cadence < 15) aiScore += 0.25;
    if (charsAdded >= 80) aiScore += 0.2;
    if (tabCorrelation && charsAdded >= 60) aiScore += 0.15;
    if (aiSignature) aiScore += 0.1;

    if (aiScore >= 0.6) {
      this.emit('ai_agent_help', {
        aiScore: Number(aiScore.toFixed(2)),
        cadence: Number(cadence.toFixed(2)),
        charsAdded,
        tabCorrelation,
      });
    }

    this.charsInBlock = 0;
    this.backspacesInBlock = 0;
  }

  ingestExternalEvent(signal, details = {}, overrides = {}) {
    this.emit(signal, details, overrides);
  }

  emit(signal, details = {}, overrides = {}) {
    const config = SIGNAL_CONFIG[signal];
    if (!config) return;

    const dedupKey = `${signal}:${JSON.stringify(details).slice(0, 80)}`;
    const now = Date.now();
    const lastAt = this.lastDedup.get(dedupKey) || 0;
    if (now - lastAt < 2000) return;
    this.lastDedup.set(dedupKey, now);

    if (signal === 'tab_switch') {
      this.lastTabSwitchAt = now;
    }

    const penalty = SEVERITY_PENALTY[overrides.severity || config.severity] || 0;
    this.trustScore = Math.max(0, this.trustScore - penalty);
    this.eventCount += 1;

    const event = {
      signal,
      confidence: overrides.confidence ?? config.confidence,
      severity: overrides.severity || config.severity,
      tier: overrides.tier || config.tier,
      message: overrides.message || config.message,
      details,
      timestamp: new Date().toISOString(),
      trust_score: this.trustScore,
      event_count: this.eventCount,
    };

    this.events = [event, ...this.events].slice(0, 100);
    this.onEvent(event);
  }

  getEvents() {
    return [...this.events];
  }

  getTrustScore() {
    return this.trustScore;
  }

  getSummary() {
    const highSeverity = this.events.filter((event) => event.severity === 'high' || event.severity === 'critical').length;
    return {
      trustScore: this.trustScore,
      totalAlerts: this.events.length,
      highSeverity,
      tabAwayMs: this.tabAwayMs,
    };
  }

  destroy() {
    if (!this.mounted) return;
    this.mounted = false;
    this.detachEditorDom();

    document.removeEventListener('visibilitychange', this.boundHandlers.visibility);
    window.removeEventListener('blur', this.boundHandlers.blur);
    document.removeEventListener('fullscreenchange', this.boundHandlers.fullscreen);
    document.removeEventListener('contextmenu', this.boundHandlers.contextmenu);
    window.removeEventListener('resize', this.boundHandlers.resize);
    window.removeEventListener('keydown', this.boundHandlers.keydown);
    window.removeEventListener('mousemove', this.boundHandlers.activity);
    window.removeEventListener('mousedown', this.boundHandlers.activity);

    if (this.idleTimer) {
      window.clearInterval(this.idleTimer);
      this.idleTimer = null;
    }
  }
}
